/**
 * @-mention helpers for tagging uploaded image/video assets in Video Studio prompts.
 *
 * Stored token format: @[Start frame](img:imageUrlUploads:0)
 * Display label:       Start frame
 * Payload expansion:   natural-language phrase (media still bound via URL arrays)
 */

/** @typedef {{
 *   key: string;
 *   token: string;
 *   label: string;
 *   kind: 'image'|'video';
 *   bucket: string;
 *   index: number;
 *   previewUrl: string;
 *   name: string;
 *   color: 'image'|'video';
 * }} MentionAsset */

export const MENTION_TOKEN_RE =
  /@\[([^\]]+)\]\((img|vid):(imageUrlUploads|referenceImageUploads|referenceVideoUploads):(\d+)\)/g;

const IMAGE_BUCKETS = ["imageUrlUploads", "referenceImageUploads"];
const VIDEO_BUCKETS = ["referenceVideoUploads"];

function labelForBucket(bucket, index, kind, customName) {
  const name = String(customName || "").trim();
  if (name && !/^upload-/i.test(name) && name.length < 40) {
    // Prefer a short clean filename stem when user-uploaded
    const stem = name.replace(/\.[a-z0-9]+$/i, "");
    if (stem && stem.length <= 28) return stem;
  }
  if (bucket === "imageUrlUploads") {
    return index === 0 ? "Start frame" : index === 1 ? "End frame" : `Frame ${index + 1}`;
  }
  if (bucket === "referenceImageUploads") {
    return index === 0 ? "Product ref" : `Product ref ${index + 1}`;
  }
  if (bucket === "referenceVideoUploads") {
    return index === 0 ? "Ref video" : `Ref video ${index + 1}`;
  }
  return kind === "video" ? `Video ${index + 1}` : `Image ${index + 1}`;
}

function isGenericFrameLabel(label) {
  return /^(Start frame|End frame|Frame \d+|Product ref(?: \d+)?|Ref video(?: \d+)?|Image \d+|Video \d+)$/i.test(
    String(label || "").trim(),
  );
}

/**
 * Natural-language stand-in for an @-token.
 * Prefer auto visual identity locks so the model preserves the uploaded likeness.
 * Named characters (Amara / Emeka) keep their identity — never rewrite them to
 * "the start-frame image" / "the end-frame image" (that destroys multi-character prompts).
 */
function phraseForAsset(asset) {
  const label = String(asset.label || "").trim();
  const identity = String(asset.visualDescription || asset.identity || "").trim();
  const name =
    label && !isGenericFrameLabel(label)
      ? label
      : asset.kind === "video"
        ? `reference video ${Number(asset.index || 0) + 1}`
        : `reference subject ${Number(asset.index || 0) + 1}`;

  if (identity) {
    return `${name} (exact likeness from the provided reference image: ${identity} — preserve face, hair, skin, wardrobe; do not reinvent)`;
  }
  if (label && !isGenericFrameLabel(label)) {
    return asset.kind === "video" ? `the character ${label}` : label;
  }
  if (asset.bucket === "imageUrlUploads") {
    if (asset.index === 0) return "the start-frame image";
    if (asset.index === 1) return "the end-frame image";
    return `reference image ${asset.index + 1}`;
  }
  if (asset.bucket === "referenceImageUploads") {
    return asset.index === 0
      ? "the product reference image"
      : `product reference image ${asset.index + 1}`;
  }
  if (asset.bucket === "referenceVideoUploads") {
    return asset.index === 0
      ? "the reference video clip"
      : `reference video ${asset.index + 1}`;
  }
  if (asset.bucket === "sceneImage") {
    return asset.index === 0
      ? "this scene's reference image"
      : `this scene's reference image ${asset.index + 1}`;
  }
  if (asset.bucket === "sceneVideo") {
    return asset.index === 0
      ? "this scene's reference video"
      : `this scene's reference video ${asset.index + 1}`;
  }
  if (asset.bucket === "studio") {
    if (label) return `the reference image “${label}”`;
    return `reference image ${Number(asset.index || 0) + 1}`;
  }
  return asset.kind === "video" ? "the referenced video" : "the referenced image";
}

function pushBucket(out, files, bucket, kind) {
  (files || []).forEach((f, index) => {
    const previewUrl = String(f?.previewUrl || "").trim();
    if (!previewUrl) return;
    const label = labelForBucket(
      bucket,
      index,
      kind,
      f.characterName || f.name || f.file?.name,
    );
    const token = `@[${label}](${kind === "video" ? "vid" : "img"}:${bucket}:${index})`;
    out.push({
      key: `${bucket}:${index}`,
      token,
      label,
      kind,
      bucket,
      index,
      previewUrl,
      name: f.characterName || f.name || f.file?.name || label,
      color: kind,
      visualDescription: String(f.visualDescription || f.identity || "").trim() || undefined,
    });
  });
}

/**
 * Collect mentionable uploads for the shared brief or a specific scene.
 * @param {object} form
 * @param {{ sceneIndex?: number }} [opts]
 * @returns {MentionAsset[]}
 */
export function collectMentionableAssets(form, opts = {}) {
  /** @type {MentionAsset[]} */
  const out = [];
  const sceneIndex = opts.sceneIndex;

  pushBucket(out, form?.imageUrlUploads, "imageUrlUploads", "image");
  pushBucket(out, form?.referenceImageUploads, "referenceImageUploads", "image");
  pushBucket(out, form?.referenceVideoUploads, "referenceVideoUploads", "video");

  if (sceneIndex != null && Array.isArray(form?.scenes) && form.scenes[sceneIndex]) {
    const scene = form.scenes[sceneIndex];
    (scene.imageUrlUploads || []).forEach((f, index) => {
      const previewUrl = String(f?.previewUrl || "").trim();
      if (!previewUrl) return;
      if (out.some((a) => a.previewUrl === previewUrl)) return;
      const label =
        index === 0
          ? `Scene ${sceneIndex + 1} image`
          : `Scene ${sceneIndex + 1} image ${index + 1}`;
      out.push({
        key: `scene-${sceneIndex}:imageUrlUploads:${index}`,
        token: `@[${label}](img:sceneImage:${index})`,
        label,
        kind: "image",
        bucket: "sceneImage",
        index,
        previewUrl,
        name: f.name || f.file?.name || label,
        color: "image",
        sceneIndex,
      });
    });
    (scene.referenceVideoUploads || []).forEach((f, index) => {
      const previewUrl = String(f?.previewUrl || "").trim();
      if (!previewUrl) return;
      if (out.some((a) => a.previewUrl === previewUrl)) return;
      const label =
        index === 0
          ? `Scene ${sceneIndex + 1} video`
          : `Scene ${sceneIndex + 1} video ${index + 1}`;
      out.push({
        key: `scene-${sceneIndex}:referenceVideoUploads:${index}`,
        token: `@[${label}](vid:sceneVideo:${index})`,
        label,
        kind: "video",
        bucket: "sceneVideo",
        index,
        previewUrl,
        name: f.name || f.file?.name || label,
        color: "video",
        sceneIndex,
      });
    });
  }

  return out;
}

/** Extended regex includes scene local buckets. */
export const MENTION_TOKEN_RE_FULL =
  /@\[([^\]]+)\]\((img|vid):(imageUrlUploads|referenceImageUploads|referenceVideoUploads|sceneImage|sceneVideo|studio):(\d+)\)/g;

/**
 * @param {string} text
 * @returns {Array<{ type: 'text'|'mention'; value: string; label?: string; kind?: string; bucket?: string; index?: number; token?: string }>}
 */
export function parsePromptSegments(text) {
  const raw = String(text || "");
  /** @type {ReturnType<typeof parsePromptSegments>} */
  const parts = [];
  const re = new RegExp(MENTION_TOKEN_RE_FULL.source, "g");
  let last = 0;
  let m;
  while ((m = re.exec(raw))) {
    if (m.index > last) {
      parts.push({ type: "text", value: raw.slice(last, m.index) });
    }
    const label = m[1];
    const kind = m[2] === "vid" ? "video" : "image";
    const bucket = m[3];
    const index = Number(m[4]);
    parts.push({
      type: "mention",
      value: m[0],
      token: m[0],
      label,
      kind,
      bucket,
      index,
    });
    last = m.index + m[0].length;
  }
  if (last < raw.length) {
    parts.push({ type: "text", value: raw.slice(last) });
  }
  if (!parts.length) parts.push({ type: "text", value: "" });
  return parts;
}

/**
 * Expand @-tokens to natural language before sending to the model.
 * Media URLs remain in image_urls / reference_*_urls.
 *
 * Speaker cues (`@[Amara](img:…): Hello`) keep the character name — never rewrite
 * to "the start-frame image" / "the end-frame image" (that breaks multi-character dialogue).
 * @param {string} text
 * @param {MentionAsset[]} [assets]
 */
export function expandMentionsInPrompt(text, assets = []) {
  const raw = String(text || "");
  const byToken = new Map(assets.map((a) => [a.token, a]));
  return raw.replace(MENTION_TOKEN_RE_FULL, (full, label, _k, bucket, index, offset) => {
    const after = raw.slice(offset + full.length);
    const name = String(label || "").trim();
    // @[Name](…): / @[Name](…) (laughing) → keep @Name for dialogue matching
    if (/^\s*(?:\([^)]{0,60}\)\s*)?:/.test(after) || /^\s*\([^)]{0,60}\)\s*(?=[\n\r])/.test(after)) {
      return name ? `@${name}` : full;
    }
    const asset = byToken.get(full);
    if (asset) return phraseForAsset(asset);
    const fake = {
      label,
      bucket,
      index: Number(index),
      kind: _k === "vid" ? "video" : "image",
    };
    return phraseForAsset(fake);
  });
}

/**
 * Expand prompts on a form clone for generation.
 * @param {object} form
 */
export function withExpandedMentionPrompts(form) {
  if (!form) return form;
  const rootAssets = collectMentionableAssets(form);
  const next = {
    ...form,
    prompt: expandMentionsInPrompt(form.prompt, rootAssets),
  };
  if (Array.isArray(form.scenes)) {
    next.scenes = form.scenes.map((scene, i) => ({
      ...scene,
      prompt: expandMentionsInPrompt(
        scene.prompt,
        collectMentionableAssets(form, { sceneIndex: i }),
      ),
    }));
  }
  if (Array.isArray(form.multiPrompt)) {
    next.multiPrompt = form.multiPrompt.map((shot) => ({
      ...shot,
      prompt: expandMentionsInPrompt(shot.prompt, rootAssets),
    }));
  }
  return next;
}

/**
 * Filter assets for the @ autocomplete query (text after @).
 * Always returns something when assets exist — unmatched queries still show the full list.
 * @param {MentionAsset[]} assets
 * @param {string} query
 */
export function filterMentionAssets(assets, query) {
  const list = assets || [];
  const q = String(query || "")
    .trim()
    .toLowerCase()
    .replace(/^@/, "");
  if (!q) return list;
  const matched = list.filter(
    (a) =>
      a.label.toLowerCase().includes(q) ||
      a.name.toLowerCase().includes(q) ||
      a.kind.toLowerCase().includes(q) ||
      a.bucket.toLowerCase().includes(q),
  );
  if (matched.length) return matched;
  // "@joh" should still surface uploads labeled "Start frame" / filenames.
  return list;
}

export { IMAGE_BUCKETS, VIDEO_BUCKETS, phraseForAsset };
